<template>
    <div class="container-fluid">
        <section class="wallet-shell">
            <div class="wallet-hero">
                <div class="wallet-hero-copy">
                    <p class="hero-eyebrow">PlePower Wallet</p>
                    <h1>Wallet history and transfer activity</h1>
                    <p class="hero-text">Review credits, debits and recent wallet activity in one clean place.</p>
                </div>
                <div class="wallet-summary-grid">
                    <div class="summary-card">
                        <span class="summary-label">Total Transactions</span>
                        <strong>{{ walletQty || 0 }}</strong>
                        <span class="summary-note">Wallet records available</span>
                    </div>
                    <div class="summary-card summary-card-soft">
                        <span class="summary-label">Account Holder</span>
                        <strong>{{ firstname }} {{ lastname }}</strong>
                        <span class="summary-note">{{ account_number || 'Transfer account pending' }}</span>
                    </div>
                </div>
            </div>

            <div class="wallet-table-card">
                <div class="contract-header">
                    <div>
                        Wallet Transactions
                        <span class="wallet-count">{{ walletQty || 0 }}</span>
                    </div>
                    <p class="wallet-table-note">Latest credits and debits on your wallet</p>
                </div>

                <div class="transaction-history-list" v-if="wallet_details && wallet_details.length">
                    <div
                        class="transaction-card"
                        v-for="(data, index) in paginatedWalletDetails"
                        :key="data.id || data.reference || index"
                    >
                        <div class="transaction-card-main">
                            <div class="transaction-icon">
                                <i :class="getTransactionIcon(data)"></i>
                            </div>

                            <div class="transaction-copy">
                                <div class="transaction-topline">
                                    <span class="transaction-title">{{ getTransactionTitle(data) }}</span>

                                    <span
                                        class="transaction-badge"
                                        :class="getBadgeClass(data)"
                                        :style="data.wallet_detail_type === 'credit'
                                            ? { backgroundColor: '#d4edda', color: '#155724' }
                                            : { backgroundColor: '#dc3545', color: '#fff' }"
                                    >
                                        {{ data.wallet_detail_type === 'credit' ? 'Credit' : 'Debit' }}
                                    </span>
                                </div>

                                <div class="transaction-meta">
                                    {{ getTransactionDescription(data) }}
                                </div>

                                <div class="transaction-extra-info">
                                    <small v-if="data.wallet_detail_type === 'credit'">
                                        Ref: {{ data.reference || 'N/A' }} • {{ data.senderBank || 'Bank Transfer' }}
                                    </small>

                                    <small v-else>
                                        {{ getDebitExtraInfo(data) }}
                                    </small>
                                </div>

                                <span class="transaction-date">
                                    {{ formatTransactionDate(data.dateOfTransaction || data.created_at) }}
                                </span>
                            </div>
                        </div>

                        <div class="transaction-amount-panel">
                            <span class="transaction-amount-label">
                                {{ data.wallet_detail_type === 'credit' ? 'Credited' : 'Debited' }}
                            </span>

                            <span
                                class="transaction-amount"
                                :class="{
                                    positive: data.wallet_detail_type === 'credit',
                                    negative: data.wallet_detail_type === 'debit'
                                }"
                            >
                                {{ data.wallet_detail_type === 'credit' ? '+' : '-' }}
                                ₦{{ formatAmount(data.amount) }}
                            </span>
                        </div>
                    </div>

                    <div class="wallet-pagination" v-if="totalPages > 1">
                        <button
                            class="wallet-page-btn"
                            type="button"
                            :disabled="currentPage === 1"
                            @click="goToPreviousPage"
                        >
                            Previous
                        </button>

                        <span class="wallet-page-status">
                            Page {{ currentPage }} of {{ totalPages }}
                        </span>

                        <button
                            class="wallet-page-btn"
                            type="button"
                            :disabled="currentPage === totalPages"
                            @click="goToNextPage"
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div class="transaction-empty-state" v-else>
                    <i class="fa fa-folder-open-o"></i>
                    <h4>No transactions yet</h4>
                    <p>Your recent wallet transaction will appear here.</p>
                </div>
            </div>
        </section>

        <p>&nbsp;</p>
        <page-loader :is-loading="isLoading"></page-loader>
    </div>
</template>

<script>
import PageLoader from '../components/PageLoader.vue'

export default {
    name: 'WalletTransactions',
    components: {
        PageLoader
    },

    data() {
        return {
            isLoading: false,
            firstname: '',
            lastname: '',
            account_number: '',
            user_id: '',
            wallet_details: [],
            walletQty: 0,
            currentPage: 1,
            pageSize: 15,
        }
    },

    computed: {
        paginatedWalletDetails() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.wallet_details.slice(start, start + this.pageSize);
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.wallet_details.length / this.pageSize));
        },
    },

    mounted() {
        this.firstname = localStorage.getItem('firstname');
        this.lastname = localStorage.getItem('lastname');
        this.account_number = localStorage.getItem('account_number');
        this.user_id = localStorage.getItem('userid');

        this.fetchAllWalletDetails(this.user_id);
    },

    methods: {
        isCredit(data) {
            return data.source === 'NIP' ||
                data.transaction_type === 'CREDIT' ||
                !!data.senderName;
        },

        getTransactionTitle(data) {
            if (this.isCredit(data)) {
                return data.senderName
                    ? this.truncateName(data.senderName)
                    : 'NIP Credit';
            }

            return data.description
                ? this.extractServiceName(data.description)
                : 'Wallet Debit';
        },

        getBadgeClass(data) {
            return this.isCredit(data) ? 'badge-credit' : 'badge-debit';
        },

        getTransactionIcon(data) {
            return this.isCredit(data)
                ? 'fa fa-arrow-circle-down text-success'
                : 'fa fa-arrow-circle-up text-danger';
        },

        getTransactionDescription(data) {
            if (this.isCredit(data)) {
                return data.senderName || 'Bank Transfer from ' + (data.senderAccountNumber || 'Unknown Bank');
            }

            return data.description || 'Wallet debit';
        },

        getDebitExtraInfo(data) {
            if (!data.description) return '';

            const desc = String(data.description).toUpperCase();

            if (/(TOKEN|PREPAID|PHCN|ELECTRICITY)/.test(desc)) {
                return 'Electricity Purchase for ' + (data.accountNumber || 'Unknown Account');
            }

            if (/AIRTIME|TOPUP/.test(desc)) {
                return 'Airtime Purchase for ' + (data.accountNumber || 'Unknown Number');
            }

            if (/DATA|BUNDLE/.test(desc)) {
                return 'Data Purchase for ' + (data.accountNumber || 'Unknown Number');
            }

            return '';
        },

        truncateName(name) {
            if (!name) return '';
            return String(name).split(' ').slice(0, 3).join(' ');
        },

        extractServiceName(desc) {
            if (!desc) return 'Wallet Debit';
            if (desc.includes('PHCN') || desc.includes('PrePaid')) return 'Electricity';
            if (desc.includes('AIRTIME')) return 'Airtime';
            if (desc.includes('DATA')) return 'Data Bundle';
            return String(desc).substring(0, 40) + '...';
        },

        formatAmount(amount) {
            return Number(amount || 0).toLocaleString('en-NG');
        },

        formatTransactionDate(dateString) {
            if (!dateString) return 'N/A';

            const date = new Date(dateString);
            return date.toLocaleString('en-NG', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        goToPreviousPage() {
            if (this.currentPage > 1) {
                this.currentPage -= 1;
            }
        },

        goToNextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage += 1;
            }
        },

        async fetchAllWalletDetails(userid) {
            try {
                this.isLoading = true

                const response = await this.$http.get('/fetch-allwallet-details', {
                })

                this.wallet_details = response.data.data || [];
                this.walletQty = this.wallet_details.length;
                this.currentPage = 1;
                this.isLoading = false
                return;

            } catch (err) {
                this.isLoading = false
                console.log(err);
            }
        },
    }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap');

.container-fluid {
    min-height: 100vh;
    padding: 1.25rem 2rem 2rem;
    background:
        radial-gradient(circle at top left, rgba(36, 86, 43, 0.12), transparent 28%),
        linear-gradient(180deg, #f4f8f5 0%, #eef3ef 100%);
}

.wallet-shell {
    max-width: 1180px;
    margin: 0 auto;
}

.wallet-hero {
    display: grid;
    grid-template-columns: 1.35fr 1fr;
    gap: 1.2rem;
    align-items: stretch;
    margin-bottom: 1.25rem;
}

.wallet-hero-copy,
.wallet-summary-grid {
    border-radius: 24px;
    box-shadow: 0 20px 45px rgba(11, 39, 24, 0.08);
}

.wallet-hero-copy {
    padding: 2rem;
    color: #fff;
    background: linear-gradient(135deg, #16372d 0%, #24562b 58%, #3b7a4d 100%);
}

.hero-eyebrow {
    margin: 0 0 0.6rem;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.72);
    font-family: Poppins;
}

.wallet-hero-copy h1 {
    margin: 0;
    font-size: 32px;
    line-height: 1.15;
    font-weight: 700;
    font-family: Poppins;
}

.hero-text {
    margin: 0.9rem 0 0;
    max-width: 520px;
    font-size: 14px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.84);
    font-family: Poppins;
}

.wallet-summary-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.72);
    backdrop-filter: blur(10px);
}

.summary-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 172px;
    padding: 1.25rem;
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f4f8f5 100%);
    border: 1px solid rgba(22, 55, 45, 0.08);
    font-family: Poppins;
}

.summary-card-soft {
    background: linear-gradient(180deg, #f5fbf6 0%, #edf7ef 100%);
}

.summary-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #6f8579;
}

.summary-card strong {
    margin: 0.85rem 0 0.55rem;
    font-size: 28px;
    line-height: 1.2;
    color: #16372d;
    word-break: break-word;
}

.summary-note {
    font-size: 12px;
    line-height: 1.6;
    color: #65766e;
}

.wallet-table-card {
    padding: 1.2rem;
    border-radius: 24px;
    background: #ffffff;
    border: 1px solid rgba(22, 55, 45, 0.07);
    box-shadow: 0 20px 45px rgba(11, 39, 24, 0.07);
}

.contract-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    background: linear-gradient(135deg, #16372d 0%, #24562b 100%);
    color: #fff;
    border-radius: 20px;
    padding: 1rem 1.2rem;
    font-size: 14px;
    font-weight: 700;
    font-family: Poppins;
    margin-bottom: 0.9rem;
}

.wallet-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 0.7rem;
    margin-left: 0.55rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    font-size: 12px;
    font-weight: 700;
}

.wallet-table-note {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
}

.transaction-history-list {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.transaction-card {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border-radius: 18px;
    border: 1px solid #edf2ee;
    background: #fff;
    box-shadow: 0 12px 30px rgba(11, 39, 24, 0.05);
}

.transaction-card-main {
    display: flex;
    gap: 0.9rem;
    align-items: flex-start;
    flex: 1;
    min-width: 0;
}

.transaction-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #eef6ef;
    color: #24562b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    font-size: 18px;
}

.transaction-copy {
    flex: 1;
    min-width: 0;
}

.transaction-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
}

.transaction-title {
    font-size: 15px;
    font-weight: 700;
    color: #16372d;
    font-family: Poppins;
}

.transaction-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.badge-credit {
    background: #d4edda !important;
    color: #155724 !important;
}

.transaction-badge.badge-debit {
    background: #dc3545 !important;
    color: #fff !important;
    border: 1px solid #dc3545;
}

.transaction-meta {
    margin-top: 0.45rem;
    font-size: 13px;
    line-height: 1.7;
    color: #65766e;
    font-family: Poppins;
}

.transaction-extra-info {
    margin-top: 0.45rem;
    font-size: 12px;
    color: #7a8d84;
    font-family: Poppins;
}

.transaction-date {
    display: inline-block;
    margin-top: 0.5rem;
    font-size: 12px;
    color: #83958d;
    font-family: Poppins;
}

.transaction-amount-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    min-width: 110px;
    padding-left: 0.75rem;
    border-left: 1px solid #edf2ee;
}

.transaction-amount-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #71867c;
}

.transaction-amount {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    font-family: Poppins;
}

.transaction-amount.positive {
    color: #1f7a3b;
}

.transaction-amount.negative {
    color: #b02a37;
}

.transaction-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 2.5rem 1.25rem;
    text-align: center;
    font-family: Poppins;
    color: #5e7267;
}

.transaction-empty-state i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: #eef6ef;
    color: #24562b;
    font-size: 22px;
}

.transaction-empty-state h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #16372d;
}

.transaction-empty-state p {
    margin: 0;
    max-width: 420px;
    font-size: 13px;
    line-height: 1.7;
}

.wallet-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    margin-top: 1rem;
    flex-wrap: wrap;
}

.wallet-page-btn {
    border: 0;
    border-radius: 999px;
    padding: 0.65rem 1rem;
    background: #24562b;
    color: #fff;
    font-family: Poppins;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.wallet-page-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.wallet-page-status {
    font-family: Poppins;
    font-size: 12px;
    font-weight: 700;
    color: #5e7267;
}

@media screen and (max-width: 900px) {
    .wallet-hero {
        grid-template-columns: 1fr;
    }
}

@media screen and (max-width: 768px) {
    .container-fluid {
        padding: 1rem;
    }

    .wallet-summary-grid {
        grid-template-columns: 1fr;
    }

    .wallet-hero-copy h1 {
        font-size: 28px;
    }

    .transaction-card {
        flex-direction: column;
        gap: 0.9rem;
        padding: 0.95rem;
        align-items: stretch;
    }

    .transaction-card-main {
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
    }

    .transaction-topline {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.45rem;
    }

    .transaction-badge {
        align-self: flex-start;
    }

    .transaction-meta,
    .transaction-extra-info {
        word-break: break-word;
    }

    .transaction-date {
        word-break: break-word;
    }

    .transaction-amount-panel {
        min-width: 0;
        width: 100%;
        padding-left: 0;
        padding-top: 0.75rem;
        border-left: 0;
        border-top: 1px solid #edf2ee;
        align-items: flex-start;
        gap: 0.4rem;
    }
}
</style>
